// 표: the workbook's grid sheets (design 02 3.5, 9, 12.1). A toolbar
// (styles, number formats, rows and columns, freezing, charts, names, xlsx),
// the formula bar, the grid, and the sheets' tabs.
//
// Keys on a PC (as Excel): arrows, Tab, Enter move (Shift goes back or
// stretches the selection), Ctrl+arrow jumps to the edge of the data,
// typing or F2 edits, Enter commits and Esc cancels (not while a Hangul
// syllable is being composed, design 8.7), Delete clears, Ctrl+C/X/V copy
// and paste (tab-separated text, so Excel's cells paste here), Ctrl+D fills
// down, Ctrl+B/I/U.
//
// Phones (design 02 9): a tap picks a cell, a second tap (or a tap on the
// formula bar, which is at the bottom there) types into it with the
// keyboard of what it holds (number keys for numbers). The keyboard's 다음
// writes the cell and goes on to the next one, down or right (↓/→), the
// keyboard staying up; a tap on another cell goes there, or puts its name
// into a formula after an operator (=, +, (, :, …); ✓ writes and puts the
// keyboard away, ✕ takes the typing back, and so does nothing else: the
// back key that puts the keyboard away writes too (lib/keyboard.ts). A row
// of keys hard to reach on a phone's keyboard (= ( ) [ ] , : " + - * /)
// goes with the bar while typing.
//
// Formulas (PC and phone, FormulaHelp.tsx): fx opens the functions by
// category (and the workbook's names); while a name is typed the ones that
// start so are offered; in a call, a guide shows its arguments with the one
// being typed marked and what goes there.
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'

import { errorText } from '../../api/client'
import { endComposition, hasFinePointer, isComposing } from '../../lib/ime'
import { holdKeyboard } from '../../lib/keyboard'
import { Icon, IconButton, Spinner } from '../../ui/common'
import { useMedia } from '../../ui/router'
import { showToast } from '../../ui/toast'
import type { Id, TensorSheet } from '../api'
import type { ChartSpec, ChartType } from '../chart'
import { builtinData, kstClock } from '../grid/builtins'
import { fillDownEdits, keepCopied, makeCopied, pasteEdits } from '../grid/clip'
import { type Dir, type Keys, type PointRef, dropBreak, keysFor, pointAt, putRef, stepOf } from '../grid/entry'
import { NUMBER_FORMATS, formatValue } from '../grid/format'
import {
  type Rect,
  type Sel,
  areaSel,
  cellName,
  cellSel,
  clampRect,
  colName,
  colPositions,
  move,
  rectName,
  rectOf,
  wholeCols,
  wholeRows,
} from '../grid/layout'
import { type CellEdit, GRID_COLS, GRID_ROWS, type Style, cellKey } from '../grid/model'
import type { GridStore } from '../grid/store'
import { scratchCalc, useGrid } from '../grid/useGrid'
import { dimsSheet, viewSheet } from '../grid/viewsheets'
import { exportBook, planImport, uniqueSheetName } from '../grid/xlsxio'
import { customDims } from '../dims'
import { savedViews, sourceOf } from '../pivot'
import { inputTensors } from '../tensors'
import type { SheetTemplate } from '../templates'
import { getState } from '../store'
import type { Current } from './common'
import { type Choice, FormulaGuide, FuncPicker, formulaNames, putInto } from './FormulaHelp'
import { GridCanvas, type HitArea } from './GridCanvas'
import { ChartSheet, ChartsOverlay, ImportResult, type MenuItem, NamesSheet, NewSheetSheet, PopMenu, SheetMenu } from './GridParts'

interface Edit {
  from: 'cell' | 'bar'
  /** typing over the cell (arrows commit) or changing it (arrows move the caret) */
  mode: 'enter' | 'edit'
  sheetId: Id
  r: number
  c: number
}

const sheetPref = (wid: Id) => `tensor.grid.sheet.${wid}`
/** where a phone's 다음 goes (this device's choice) */
const NEXT_PREF = 'tensor.grid.next'

/** Keys a phone's keyboard keeps out of the way for formulas. */
const FORMULA_KEYS = ['=', '(', ')', '[', ']', ',', ':', '"', '+', '-', '*', '/', 'SUM(']

function readPref(key: string): string | null {
  try {
    return localStorage.getItem(key)
  } catch {
    return null
  }
}

function writePref(key: string, v: string): void {
  try {
    localStorage.setItem(key, v)
  } catch {
    // only a convenience
  }
}

function newSheetName(sheets: TensorSheet[]): string {
  const taken = new Set(sheets.map((s) => s.name.toLowerCase()))
  for (let k = sheets.length + 1; ; k++) if (!taken.has(`시트${k}`)) return `시트${k}`
}

export function GridScreen({ cur }: { cur: Current }) {
  const store = useGrid(cur.workbook.id)
  if (store.status === 'error')
    return (
      <div className="empty">
        <p>표를 읽지 못했습니다.</p>
        <p className="small">{store.error}</p>
      </div>
    )
  if (store.status === 'loading')
    return (
      <div className="empty">
        <Spinner label="표를 읽는 중" />
      </div>
    )
  return <GridReady store={store} cur={cur} />
}

function GridReady({ store, cur }: { store: GridStore; cur: Current }) {
  const wid = cur.workbook.id
  const sheets = store.sheets()
  const fine = useMedia('(pointer: fine)') && hasFinePointer()
  const [sheetId, setSheetId] = useState<Id>(() => Number(readPref(sheetPref(wid))) || 0)
  const sheet = sheets.find((s) => s.id === sheetId) ?? sheets[0]
  const [sel, setSel] = useState<Sel>(cellSel(0, 0))
  const [edit, setEditState] = useState<Edit | null>(null)
  const [draft, setDraftState] = useState('')
  // the edit and its text as they are now, for events that come before the
  // screen is drawn again (a blur right after a commit)
  const editRef = useRef<Edit | null>(null)
  const draftRef = useRef('')
  const setEdit = (e: Edit | null) => {
    editRef.current = e
    setEditState(e)
  }
  const setDraft = (t: string) => {
    draftRef.current = t
    setDraftState(t)
  }
  // a phone's typing: where 다음 goes, the keyboard picked by hand, the cell a tap put into the formula
  const [dir, setDir] = useState<Dir>(() => (readPref(NEXT_PREF) === 'right' ? 'right' : 'down'))
  const [keysOver, setKeysOver] = useState<Keys | null>(null)
  const [point, setPoint] = useState<(PointRef & { rect: Rect }) | null>(null)
  const composing = useRef(false)
  // the caret in the box being typed (the guide), and the functions open
  const [caret, setCaret] = useState(0)
  const [picker, setPicker] = useState(false)
  /** the bar gives up the focus and takes it back on purpose (endComposition) */
  const holdBlur = useRef(false)
  /** the text of the cell typed into next is picked (typing replaces it) */
  const pickAll = useRef(false)
  const [reveal, setReveal] = useState<{ r: number; c: number; n: number } | null>(null)
  const [menu, setMenu] = useState<{ x: number; y: number; area: HitArea } | null>(null)
  const [sheetMenu, setSheetMenu] = useState<TensorSheet | null>(null)
  const [names, setNames] = useState(false)
  const [newSheet, setNewSheet] = useState(false)
  const [chart, setChart] = useState(false)
  const [imported, setImported] = useState<{ sheets: string[]; names: number; skipped: string[] } | null>(null)
  const [busyText, setBusyText] = useState<string | null>(null)
  const [widthDrag, setWidthDrag] = useState<{ sheetId: Id; col: number; w: number } | null>(null)
  const editor = useRef<HTMLTextAreaElement>(null)
  const bar = useRef<HTMLTextAreaElement>(null)
  const file = useRef<HTMLInputElement>(null)
  const revealN = useRef(0)

  useEffect(() => {
    if (sheet) writePref(sheetPref(wid), String(sheet.id))
  }, [wid, sheet])

  const readOnly = !sheet || store.busy(sheet.id)
  const rect = rectOf(sel)
  const activeCell = sheet ? store.cell(sheet.id, sel.ar, sel.ac) : undefined
  const activeInput = activeCell?.input ?? ''

  const widths = useMemo(() => {
    if (!sheet) return {}
    if (widthDrag && widthDrag.sheetId === sheet.id) return { ...sheet.col_widths, [String(widthDrag.col)]: widthDrag.w }
    return sheet.col_widths
  }, [sheet, widthDrag])

  const focusGrid = useCallback(() => {
    if (fine) editor.current?.focus({ preventScroll: true })
  }, [fine])

  const showCell = (r: number, c: number) => setReveal({ r, c, n: ++revealN.current })

  // ---- editing ----

  const begin = (from: Edit['from'], mode: Edit['mode'], text: string | null) => {
    if (!sheet || readOnly) return false
    setEdit({ from, mode, sheetId: sheet.id, r: sel.ar, c: sel.ac })
    const t = text ?? editor.current?.value ?? ''
    setDraft(t)
    setKeysOver(null)
    setPoint(null)
    if (from === 'cell' && text !== null && editor.current) {
      editor.current.value = text
      editor.current.setSelectionRange(text.length, text.length)
    }
    return true
  }

  const finishEdit = () => {
    setEdit(null)
    setKeysOver(null)
    setPoint(null)
    setPicker(false)
    if (editor.current) editor.current.value = ''
  }

  /** Writes an edit's text into its cell (when it changed). */
  const write = (e: Edit, text: string) => {
    const now = store.cell(e.sheetId, e.r, e.c)
    if (text !== (now?.input ?? '')) store.setCells(e.sheetId, [{ row: e.r, col: e.c, input: text }])
  }

  const commit = (text: string, dr = 0, dc = 0) => {
    const e = editRef.current
    finishEdit()
    if (!e) return
    write(e, text)
    if (dr || dc) {
      const m = move(e.r, e.c, dr, dc)
      setSel(cellSel(m.r, m.c))
      showCell(m.r, m.c)
    }
    focusGrid()
  }

  /** A phone's typing is over: the bar gives up the focus (the keyboard goes). */
  const leaveBar = () => {
    if (!fine && bar.current && document.activeElement === bar.current) bar.current.blur()
  }

  const cancel = () => {
    finishEdit()
    leaveBar()
    focusGrid()
  }

  // leaving a sheet ends the edit
  useEffect(() => {
    if (edit && sheet && edit.sheetId !== sheet.id) {
      finishEdit()
      leaveBar()
    }
  }, [sheet, edit]) // eslint-disable-line react-hooks/exhaustive-deps

  // leaving the screen (another tab, the back key) writes what was typed
  useEffect(
    () => () => {
      const e = editRef.current
      editRef.current = null
      if (e) write(e, draftRef.current)
    },
    [store], // eslint-disable-line react-hooks/exhaustive-deps
  )

  // ---- selection ----

  const select = (s: Sel, how: { drag: boolean; area: HitArea; double?: boolean }) => {
    const e = editRef.current
    if (e && !how.drag) {
      if (e.from === 'cell') commit(editor.current?.value ?? draftRef.current)
      else {
        commit(bar.current?.value ?? draftRef.current)
        leaveBar()
      }
    }
    setSel(s)
    if (how.double && fine) {
      setTimeout(() => editInCell(s), 0)
      return
    }
    // The editor takes the keys now, in this pointer event: a timer may run
    // only after the next key presses (the browser handles input first).
    if (!how.drag) focusGrid()
  }

  /** Edits a cell in place (a PC's double click, a touch screen PC's second tap). */
  const editInCell = (s: Sel) => {
    setSel(s)
    if (readOnly || !sheet) return
    const input = store.cell(sheet.id, s.ar, s.ac)?.input ?? ''
    setEdit({ from: 'cell', mode: 'edit', sheetId: sheet.id, r: s.ar, c: s.ac })
    setDraft(input)
    if (editor.current) {
      editor.current.value = input
      editor.current.focus({ preventScroll: true })
    }
  }

  // ---- typing on a phone (design 02 9) ----

  const phoneEdit = !fine && edit?.from === 'bar'

  /** Finishes a Hangul syllable being composed in the bar, which keeps the focus (design 8.7). */
  const settle = (el: HTMLTextAreaElement) => {
    if (!composing.current) return
    holdKeyboard()
    holdBlur.current = true
    try {
      endComposition(el)
    } finally {
      holdBlur.current = false
    }
    composing.current = false
  }

  /** The keyboard for a cell: by what it holds, else by the cell before it (grid/entry.ts). */
  const keysAt = (r: number, c: number): Keys => {
    if (!sheet) return 'text'
    const [dr, dc] = stepOf(dir)
    const before = r - dr >= 0 && c - dc >= 0 ? (store.cell(sheet.id, r - dr, c - dc)?.input ?? '') : ''
    return keysFor(store.cell(sheet.id, r, c)?.input ?? '', before)
  }
  const keys: Keys = keysOver ?? (edit ? keysAt(edit.r, edit.c) : keysAt(sel.ar, sel.ac))

  /** Types into (r, c) next, the bar keeping the focus and the keyboard. */
  const typeNext = (r: number, c: number) => {
    const e = editRef.current
    if (!e) return
    setEdit({ ...e, from: 'bar', mode: 'edit', r, c })
    setDraft(store.cell(e.sheetId, r, c)?.input ?? '')
    setPoint(null)
    setSel(cellSel(r, c))
    showCell(r, c)
    pickAll.current = true
  }

  // the next cell's text is picked: typing replaces it, a tap in it places the caret
  useLayoutEffect(() => {
    if (!pickAll.current) return
    pickAll.current = false
    const el = bar.current
    if (el && document.activeElement === el) el.select()
  })

  /** The keyboard's 다음: writes the cell, then the next one down (or right). */
  const advance = (text: string) => {
    const e = editRef.current
    if (!e) return
    write(e, text)
    const [dr, dc] = stepOf(dir)
    const m = move(e.r, e.c, dr, dc)
    typeNext(m.r, m.c)
  }

  /** ✓: writes the cell; the keyboard goes. */
  const done = () => {
    const el = bar.current
    if (el) settle(el)
    commit(el?.value ?? draftRef.current)
    leaveBar()
  }

  /** A tap while typing on a phone: that cell's name into the formula (after an
      operator), or on to that cell. False: a tap this does not take. */
  const tapWhileTyping = (r: number, c: number, area: HitArea): boolean => {
    const el = bar.current
    const e = editRef.current
    if (!el || !e) return false
    settle(el)
    const ref = area === 'cell' ? cellName(r, c) : area === 'col' ? `${colName(c)}:${colName(c)}` : area === 'row' ? `${r + 1}:${r + 1}` : null
    const at = ref ? pointAt(el.value, el.selectionStart, el.selectionEnd, point) : null
    if (ref && at) {
      const put = putRef(el.value, at, ref)
      el.value = put.text
      el.setSelectionRange(put.caret, put.caret)
      setDraft(put.text)
      setCaret(put.caret)
      setPoint({ from: at.from, to: put.caret, ref, rect: rectOf(areaSel(area, r, c)) })
      return true
    }
    if (area !== 'cell') return false
    write(e, el.value)
    typeNext(r, c)
    return true
  }

  /** A second tap on the picked cell: typing into it (the bar on a phone, in place on a touch screen PC). */
  const typeHere = () => {
    if (!sheet || readOnly) return
    if (fine) return editInCell(sel)
    const el = bar.current
    if (!el) return
    // in this tap, so that the keyboard comes up; the bar's focus starts the
    // edit (or this, when it had the focus already: no focus event)
    el.focus({ preventScroll: true })
    if (!editRef.current && !begin('bar', 'edit', activeInput)) return
    el.setSelectionRange(el.value.length, el.value.length)
  }

  /** A finger's tap on the grid. */
  const onTap = (r: number, c: number, area: HitArea) => {
    const e = editRef.current
    if (e && e.from === 'bar' && !fine) {
      if (tapWhileTyping(r, c, area)) return
      done()
    }
    const again = !e && area === 'cell' && sel.ar === r && sel.ac === c && sel.fr === r && sel.fc === c
    select(areaSel(area, r, c), { drag: false, area })
    if (again) typeHere()
  }

  /** Puts a key's text in at the caret (the row of keys). */
  const putKey = (t: string) => {
    const el = bar.current
    if (!el || !editRef.current) return
    settle(el)
    el.setRangeText(t, el.selectionStart, el.selectionEnd, 'end')
    setDraft(el.value)
    setCaret(el.selectionStart)
    setPoint(null)
  }

  /** The box being typed in (a PC's cell or the bar); a PC not typing yet starts in the bar. */
  const typingBox = (): HTMLTextAreaElement | null => {
    const e = editRef.current
    if (e?.from === 'cell') return editor.current
    if (!e) {
      if (readOnly) return null
      bar.current?.focus()
      if (!editRef.current && !begin('bar', 'edit', activeInput)) return null
    }
    return bar.current
  }

  /** A function (NAME() or a name from the guide or the picker, into the box being typed in. */
  const putChoice = (c: Choice) => {
    const el = typingBox()
    if (!el) return
    if (el === bar.current) settle(el)
    setDraft(putInto(el, c))
    setCaret(el.selectionStart)
    setPoint(null)
    setPicker(false)
  }

  // the names formulas use
  const book = cur.book
  const nameList = store.model.nameList()
  const formulaList = useMemo(() => formulaNames(book, nameList), [book, nameList])

  const flipKeys = () => {
    const el = bar.current
    if (el) settle(el)
    // the keyboard is swapped: not its going away (lib/keyboard.ts)
    holdKeyboard()
    setKeysOver(keys === 'num' ? 'text' : 'num')
  }

  const flipDir = () => {
    const d: Dir = dir === 'down' ? 'right' : 'down'
    setDir(d)
    writePref(NEXT_PREF, d)
  }

  // the keyboard comes and goes: the cell being typed stays in view
  useEffect(() => {
    if (!phoneEdit) return
    const keep = () => {
      const e = editRef.current
      if (e) showCell(e.r, e.c)
    }
    window.addEventListener('resize', keep)
    return () => window.removeEventListener('resize', keep)
  }, [phoneEdit]) // eslint-disable-line react-hooks/exhaustive-deps

  /** The phone bar's buttons leave the focus in the bar (the keyboard stays up). */
  const keepFocus = (e: React.PointerEvent) => e.preventDefault()

  /** The used edge from (r, c) going (dr, dc): Excel's Ctrl+arrow. */
  const jump = (r: number, c: number, dr: number, dc: number): { r: number; c: number } => {
    if (!sheet) return { r, c }
    const has = (rr: number, cc: number) => {
      const x = store.cell(sheet.id, rr, cc)
      return !!x && (x.input !== '' || x.value !== null)
    }
    const used = store.used(sheet.id)
    const maxR = dr ? Math.max(used.rows, 0) : GRID_ROWS - 1
    const maxC = dc ? Math.max(used.cols, 0) : GRID_COLS - 1
    let rr = r
    let cc = c
    const step = () => {
      rr += dr
      cc += dc
    }
    const inside = () => rr >= 0 && cc >= 0 && rr <= Math.max(maxR, r) && cc <= Math.max(maxC, c)
    const start = has(r, c)
    step()
    if (!inside()) return dr > 0 || dc > 0 ? { r: dr ? Math.max(r, used.rows) : r, c: dc ? Math.max(c, used.cols) : c } : { r: Math.max(0, rr), c: Math.max(0, cc) }
    if (start && has(rr, cc)) {
      while (inside() && has(rr + dr, cc + dc)) step()
      return { r: rr, c: cc }
    }
    while (inside() && !has(rr, cc)) step()
    if (!inside()) return { r: Math.max(0, Math.min(rr - dr, GRID_ROWS - 1)), c: Math.max(0, Math.min(cc - dc, GRID_COLS - 1)) }
    return { r: rr, c: cc }
  }

  const moveSel = (dr: number, dc: number, extend: boolean, far: boolean) => {
    const from = extend ? { r: sel.fr, c: sel.fc } : { r: sel.ar, c: sel.ac }
    const to = far ? jump(from.r, from.c, dr, dc) : move(from.r, from.c, dr, dc)
    if (extend) setSel({ ...sel, fr: to.r, fc: to.c })
    else setSel(cellSel(to.r, to.c))
    showCell(to.r, to.c)
  }

  // ---- what the toolbar and keys do ----

  /** The selected cells that exist or matter (whole columns/rows: the used part). */
  const cellsOf = (r: Rect): { row: number; col: number }[] => {
    if (!sheet) return []
    const box = clampRect(r, store.used(sheet.id))
    const out: { row: number; col: number }[] = []
    const n = (box.r2 - box.r1 + 1) * (box.c2 - box.c1 + 1)
    if (n > 200_000) {
      showToast('한 번에 20만 칸까지 바꿀 수 있어요.')
      return []
    }
    for (let row = box.r1; row <= box.r2; row++) for (let col = box.c1; col <= box.c2; col++) out.push({ row, col })
    return out
  }

  const clear = () => {
    if (!sheet || readOnly) return
    const edits: CellEdit[] = []
    for (const { row, col } of cellsOf(rect)) {
      const c = store.cell(sheet.id, row, col)
      if (c && c.input !== '') edits.push({ row, col, input: '' })
    }
    store.setCells(sheet.id, edits)
  }

  const style = (patch: Partial<Record<keyof Style, unknown>>) => {
    if (!sheet || readOnly) return
    store.setStyle(sheet.id, cellsOf(rect), patch)
    focusGrid()
  }

  const toggle = (k: 'b' | 'i' | 'u') => style({ [k]: !activeCell?.style?.[k] })

  const blockOf = (r: Rect) => {
    const box = sheet ? clampRect(r, store.used(sheet.id)) : r
    const texts: string[][] = []
    const inputs: string[][] = []
    const styles: (Style | null)[][] = []
    for (let row = box.r1; row <= box.r2; row++) {
      const t: string[] = []
      const i: string[] = []
      const s: (Style | null)[] = []
      for (let col = box.c1; col <= box.c2; col++) {
        const c = sheet ? store.cell(sheet.id, row, col) : undefined
        t.push(c ? formatValue(c.value, c.style?.nf, c.input).text : '')
        i.push(c?.input ?? '')
        s.push(c?.style ?? null)
      }
      texts.push(t)
      inputs.push(i)
      styles.push(s)
    }
    return { box, texts, inputs, styles }
  }

  const doCopy = (data: DataTransfer | null) => {
    if (!sheet || !data) return
    const b = blockOf(rect)
    const c = makeCopied(sheet.id, b.box.r1, b.box.c1, b.texts, b.inputs, b.styles)
    keepCopied(c)
    data.setData('text/plain', c.tsv)
  }

  const doPaste = async (text: string) => {
    if (!sheet || readOnly || text === '') return
    const fill = wholeCols(rect) || wholeRows(rect) ? undefined : { r2: rect.r2, c2: rect.c2 }
    const edits = await pasteEdits(text, rect.r1, rect.c1, (items) => store.translateAll(items), fill)
    const tooFar = edits.some((e) => e.row >= GRID_ROWS || e.col >= GRID_COLS)
    store.setCells(sheet.id, edits.filter((e) => e.row < GRID_ROWS && e.col < GRID_COLS))
    if (tooFar) showToast('표 끝을 넘는 칸은 빼고 붙였어요.')
    if (edits.length) {
      const last = edits[edits.length - 1]!
      setSel({ ar: rect.r1, ac: rect.c1, fr: Math.max(rect.r2, last.row), fc: Math.max(rect.c2, last.col) })
    }
  }

  const fillDown = async () => {
    if (!sheet || readOnly) return
    const box = clampRect(rect, store.used(sheet.id))
    if (box.r2 <= box.r1) return
    const top = blockOf({ r1: box.r1, c1: box.c1, r2: box.r1, c2: box.c2 }).inputs[0] ?? []
    store.setCells(sheet.id, await fillDownEdits(top, box.r1, box.c1, box.r2, (items) => store.translateAll(items)))
  }

  // ---- the cell editor's keys (PC) ----

  const onEditorKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (isComposing(e)) return
    const ctrl = e.ctrlKey || e.metaKey
    const ed = editRef.current
    if (ed && ed.from === 'cell') {
      const el = e.currentTarget
      if (e.key === 'Enter' && e.altKey) {
        e.preventDefault()
        el.setRangeText('\n', el.selectionStart, el.selectionEnd, 'end')
        setDraft(el.value)
      } else if (e.key === 'Enter') {
        e.preventDefault()
        commit(el.value, e.shiftKey ? -1 : 1, 0)
      } else if (e.key === 'Tab') {
        e.preventDefault()
        commit(el.value, 0, e.shiftKey ? -1 : 1)
      } else if (e.key === 'Escape') {
        e.preventDefault()
        cancel()
      } else if (ed.mode === 'enter' && e.key.startsWith('Arrow')) {
        e.preventDefault()
        const d = { ArrowUp: [-1, 0], ArrowDown: [1, 0], ArrowLeft: [0, -1], ArrowRight: [0, 1] }[e.key] ?? [0, 0]
        commit(el.value, d[0], d[1])
      } else if (e.key === 'F2') {
        e.preventDefault()
        setEdit({ ...ed, mode: ed.mode === 'enter' ? 'edit' : 'enter' })
      }
      return
    }
    const arrows: Record<string, [number, number]> = { ArrowUp: [-1, 0], ArrowDown: [1, 0], ArrowLeft: [0, -1], ArrowRight: [0, 1] }
    const d = arrows[e.key]
    if (d) {
      e.preventDefault()
      moveSel(d[0], d[1], e.shiftKey, ctrl)
    } else if (e.key === 'Enter' || e.key === 'Tab') {
      e.preventDefault()
      const back = e.shiftKey ? -1 : 1
      const multi = rect.r1 !== rect.r2 || rect.c1 !== rect.c2
      if (multi && !wholeCols(rect) && !wholeRows(rect)) {
        // move inside the selection
        let r = sel.ar
        let c = sel.ac
        if (e.key === 'Enter') r += back
        else c += back
        if (c > rect.c2) ((c = rect.c1), (r += 1))
        if (c < rect.c1) ((c = rect.c2), (r -= 1))
        if (r > rect.r2) r = rect.r1
        if (r < rect.r1) r = rect.r2
        setSel({ ...sel, ar: r, ac: c })
        showCell(r, c)
      } else moveSel(e.key === 'Enter' ? back : 0, e.key === 'Tab' ? back : 0, false, false)
    } else if (e.key === 'Home') {
      e.preventDefault()
      const r = ctrl ? 0 : sel.ar
      setSel(cellSel(r, 0))
      showCell(r, 0)
    } else if (e.key === 'PageDown' || e.key === 'PageUp') {
      e.preventDefault()
      moveSel(e.key === 'PageDown' ? 20 : -20, 0, e.shiftKey, false)
    } else if (e.key === 'F2') {
      e.preventDefault()
      begin('cell', 'edit', activeInput)
    } else if (e.key === 'Delete') {
      e.preventDefault()
      clear()
    } else if (e.key === 'Backspace') {
      e.preventDefault()
      if (begin('cell', 'enter', '')) setDraft('')
    } else if (e.key === 'Escape') {
      keepCopied(null)
    } else if (ctrl && (e.key === 'a' || e.key === 'A')) {
      e.preventDefault()
      setSel({ ar: 0, ac: 0, fr: GRID_ROWS - 1, fc: GRID_COLS - 1 })
    } else if (ctrl && (e.key === 'd' || e.key === 'D')) {
      e.preventDefault()
      void fillDown()
    } else if (ctrl && ['b', 'i', 'u', 'B', 'I', 'U'].includes(e.key)) {
      e.preventDefault()
      toggle(e.key.toLowerCase() as 'b' | 'i' | 'u')
    }
  }

  const editorProps: React.TextareaHTMLAttributes<HTMLTextAreaElement> = {
    onKeyDown: onEditorKey,
    onCompositionStart: () => {
      if (!editRef.current) begin('cell', 'enter', null)
    },
    onInput: (e) => {
      const v = e.currentTarget.value
      if (!editRef.current) {
        if (!begin('cell', 'enter', null)) {
          e.currentTarget.value = ''
          return
        }
      }
      setDraft(v)
      setCaret(e.currentTarget.selectionStart)
    },
    onSelect: (e) => setCaret(e.currentTarget.selectionStart),
    onBlur: (e) => {
      if (editRef.current?.from === 'cell') {
        const next = e.relatedTarget as HTMLElement | null
        if (next === bar.current) return
        commit(e.currentTarget.value)
      }
    },
    onCopy: (e) => {
      if (edit) return
      e.preventDefault()
      doCopy(e.clipboardData)
    },
    onCut: (e) => {
      if (edit) return
      e.preventDefault()
      doCopy(e.clipboardData)
      clear()
    },
    onPaste: (e) => {
      if (edit) return
      e.preventDefault()
      void doPaste(e.clipboardData.getData('text/plain'))
    },
  }

  // ---- the formula bar ----

  const barValue = edit ? draft : activeInput
  const onBarKey = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (isComposing(e)) return
    if (e.key === 'Escape' && picker) {
      e.preventDefault()
      setPicker(false)
    } else if (e.key === 'Escape') {
      e.preventDefault()
      cancel()
    } else if (e.key === 'Enter' && !fine) {
      // a phone keyboard's 다음
      if (!editRef.current) return
      e.preventDefault()
      advance(e.currentTarget.value)
    } else if (e.key === 'Enter' && fine && !e.shiftKey && !e.altKey) {
      e.preventDefault()
      commit(e.currentTarget.value, 1, 0)
    } else if (e.key === 'Tab' && fine) {
      e.preventDefault()
      commit(e.currentTarget.value, 0, e.shiftKey ? -1 : 1)
    }
  }

  // ---- rows, columns, sheets ----

  const structure = (axis: 'row' | 'col', where: 'before' | 'after' | 'delete') => {
    if (!sheet || readOnly) return
    const rows = wholeCols(rect) ? 1 : rect.r2 - rect.r1 + 1
    const cols = wholeRows(rect) ? 1 : rect.c2 - rect.c1 + 1
    const n = axis === 'row' ? rows : cols
    const first = axis === 'row' ? (wholeCols(rect) ? sel.ar : rect.r1) : wholeRows(rect) ? sel.ac : rect.c1
    if (where === 'delete') store.structure(sheet.id, axis, first, -n)
    else store.structure(sheet.id, axis, where === 'before' ? first : first + n, n)
  }

  const menuItems: MenuItem[] = [
    { label: '위에 행 넣기', onPick: () => structure('row', 'before'), disabled: readOnly },
    { label: '아래에 행 넣기', onPick: () => structure('row', 'after'), disabled: readOnly },
    { label: '왼쪽에 열 넣기', onPick: () => structure('col', 'before'), disabled: readOnly },
    { label: '오른쪽에 열 넣기', onPick: () => structure('col', 'after'), disabled: readOnly },
    { label: '행 지우기', onPick: () => structure('row', 'delete'), danger: true, disabled: readOnly },
    { label: '열 지우기', onPick: () => structure('col', 'delete'), danger: true, disabled: readOnly },
    { label: '내용 지우기', onPick: clear, disabled: readOnly },
  ]
  if (fine && menu?.area === 'col') menuItems.splice(4, 1)
  if (fine && menu?.area === 'row') menuItems.splice(5, 1)

  const addSheet = async () => {
    try {
      const s = await store.addSheet(newSheetName(sheets))
      setSheetId(s.id)
      setSel(cellSel(0, 0))
    } catch (e) {
      showToast(errorText(e))
    }
  }

  const addFromTemplate = async (t: SheetTemplate) => {
    setBusyText(`'${t.title}' 서식을 만드는 중…`)
    try {
      const name = uniqueSheetName(t.name, new Set(store.sheets().map((x) => x.name.toLowerCase())))
      const s = await store.addSheet(name)
      await store.uploadCells(
        s.id,
        t.cells.map((c) => ({ row: c.row, col: c.col, input: c.input, ...(c.style ? { style: c.style } : {}) })),
      )
      await store.updateSheet(s.id, { col_widths: t.colWidths, ...(t.frozenRows ? { frozen_rows: t.frozenRows } : {}) })
      setSheetId(s.id)
      setSel(cellSel(0, 0))
    } catch (e) {
      showToast(errorText(e))
    } finally {
      setBusyText(null)
    }
  }

  const patchSheet = (body: Parameters<GridStore['updateSheet']>[1]) => {
    if (!sheet) return
    store.updateSheet(sheet.id, body).catch((e) => showToast(errorText(e)))
  }

  const freeze = () => {
    if (!sheet) return
    if (sheet.frozen_rows || sheet.frozen_cols) patchSheet({ frozen_rows: 0, frozen_cols: 0 })
    else {
      const r = Math.min(100, sel.ar || 1)
      const c = Math.min(50, sel.ar ? sel.ac : 0)
      patchSheet({ frozen_rows: r, frozen_cols: c })
    }
  }

  const onResize = (col: number, w: number, done: boolean) => {
    if (!sheet) return
    setWidthDrag({ sheetId: sheet.id, col, w })
    if (done) {
      store
        .updateSheet(sheet.id, { col_widths: { ...sheet.col_widths, [String(col)]: w } })
        .catch((e) => showToast(errorText(e)))
        .finally(() => setWidthDrag(null))
    }
  }

  const createChart = async (type: ChartType, range: string, title: string) => {
    if (!sheet) return
    const xs = colPositions(sheet.col_widths, rect.c2 + 2)
    const spec: ChartSpec = {
      id: Math.random().toString(36).slice(2, 10),
      type,
      range,
      ...(title ? { title } : {}),
      x: Math.round((xs[Math.min(rect.c2, GRID_COLS - 2) + 1] ?? 0) + 16),
      y: Math.round((wholeCols(rect) ? 0 : rect.r1) * 24),
      w: 420,
      h: 280,
    }
    await store.updateSheet(sheet.id, { charts: [...sheet.charts, spec] as never })
  }

  // ---- xlsx ----

  const importFile = async (f: File) => {
    setBusyText('xlsx를 읽는 중…')
    try {
      const bytes = new Uint8Array(await f.arrayBuffer())
      const plan = await planImport(
        bytes,
        { translateAll: (items) => store.translateAll(items) },
        { sheets: store.sheets().map((s) => s.name), names: store.model.nameList().map((n) => n.name) },
        scratchCalc,
      )
      const made: string[] = []
      const skipped = [...plan.skipped]
      let first: Id = 0
      for (const s of plan.sheets) {
        setBusyText(`'${s.name}' 시트를 올리는 중…`)
        const created = await store.addSheet(s.name)
        if (!first) first = created.id
        await store.uploadCells(created.id, s.cells, (n) => setBusyText(`'${s.name}' ${n.toLocaleString()} / ${s.cells.length.toLocaleString()}칸`))
        if (s.patch) await store.updateSheet(created.id, s.patch)
        made.push(s.name)
      }
      let names = 0
      for (const n of plan.names) {
        try {
          await store.addName(n.name, n.formula)
          names++
        } catch (e) {
          skipped.push(`이름 ${n.name}: ${errorText(e)}`)
        }
      }
      if (first) {
        setSheetId(first)
        setSel(cellSel(0, 0))
      }
      setImported({ sheets: made, names, skipped })
    } catch (e) {
      showToast(e instanceof Error ? e.message : errorText(e))
    } finally {
      setBusyText(null)
    }
  }

  const exportFile = async () => {
    setBusyText('xlsx를 만드는 중…')
    try {
      await store.settle()
      const book = getState().books[wid]
      const data = builtinData(cur.rows, book?.items ?? {}, book?.budgets ?? {}, monthNow(), {
        dims: book?.dims,
        tensors: book?.tensors,
        entries: book?.entries,
      })
      // the saved views: a sheet each, from the engine's answers (design 02 8.3)
      const views = savedViews(book?.views)
      const answers = await store.evaluate(views.map((v) => '=' + v.config.source))
      const viewSheets = views.map((v, i) => {
        const a = answers[i]
        const s = a?.ok ? sourceOf(a.value) : { error: a?.error ?? '#CALC!' }
        return viewSheet({ name: v.name, config: v.config, ...('error' in s ? { src: null, error: s.error } : { src: s }) }, monthNow())
      })
      const bytes = await exportBook(
        store.sheets().map((s) => {
          const cells = new Map<number, { input: string; style: Style | null }>()
          for (const k of store.model.keysOf(s.id)) {
            const c = store.cell(s.id, Math.floor(k / GRID_COLS), k % GRID_COLS)
            if (c) cells.set(cellKey(Math.floor(k / GRID_COLS), k % GRID_COLS), { input: c.input, style: c.style })
          }
          return { sheet: s, cells, values: store.values.get(s.id) ?? new Map() }
        }),
        store.model.nameList(),
        data,
        {
          names: [...inputTensors(book?.tensors).map((t) => t.name), ...customDims(book?.dims).map((d) => d.name)],
          sheets: [dimsSheet(data, book?.dims), ...viewSheets],
        },
      )
      const blob = new Blob([bytes as BlobPart], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
      const a = document.createElement('a')
      a.href = URL.createObjectURL(blob)
      a.download = `${cur.workbook.name.replace(/[\\/:*?"<>|]/g, '_') || 'tensor'}.xlsx`
      document.body.appendChild(a)
      a.click()
      a.remove()
      setTimeout(() => URL.revokeObjectURL(a.href), 10_000)
    } catch (e) {
      showToast(errorText(e))
    } finally {
      setBusyText(null)
    }
  }

  // ---- drawing ----

  if (!sheet)
    return (
      <div className="empty">
        <p>아직 표 시트가 없습니다.</p>
        <p className="small">엑셀처럼 칸에 수와 수식을 적습니다. 거래·지출·예산 같은 이름도 수식에서 쓸 수 있습니다.</p>
        <div className="row-buttons">
          <button type="button" className="primary" onClick={() => void addSheet()}>
            첫 시트 만들기
          </button>
          <button type="button" className="secondary" onClick={() => setNewSheet(true)}>
            서식에서 고르기
          </button>
          <button type="button" className="secondary" onClick={() => file.current?.click()}>
            xlsx 가져오기
          </button>
        </div>
        <input ref={file} type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" hidden onChange={(e) => {
          const f = e.target.files?.[0]
          e.target.value = ''
          if (f) void importFile(f)
        }} />
        {busyText && <p role="status">{busyText}</p>}
        <ImportResult result={imported} onClose={() => setImported(null)} />
        <NewSheetSheet open={newSheet} onClose={() => setNewSheet(false)} onBlank={() => void addSheet()} onTemplate={(t) => void addFromTemplate(t)} />
      </div>
    )

  const st = activeCell?.style
  const pending = store.pendingCount()

  const fx = (
    <button
      type="button"
      className={`g-fx${picker ? ' on' : ''}`}
      aria-label="함수"
      aria-pressed={picker}
      title="함수"
      onPointerDown={keepFocus}
      onClick={() => {
        if (!picker && !editRef.current && !typingBox()) return
        setPicker(!picker)
      }}
      disabled={readOnly}
    >
      <i>fx</i>
    </button>
  )
  // the guide while a formula is typed
  const guide = edit !== null && (
    <FormulaGuide text={draft} caret={caret} names={formulaList} onPick={putChoice} keep={keepFocus} />
  )
  const funcs = picker && edit !== null && <FuncPicker names={formulaList} onPick={putChoice} onClose={() => setPicker(false)} keep={keepFocus} />

  const formulaBar = (
    <div className="g-bar">
      <span className="g-ref" aria-label="고른 칸">
        {rectName(rect)}
      </span>
      {fine && fx}
      <textarea
        ref={bar}
        className="g-formula"
        aria-label="수식 입력줄"
        rows={1}
        value={barValue}
        readOnly={readOnly}
        spellCheck={false}
        autoComplete="off"
        inputMode={fine ? undefined : keys === 'num' ? 'decimal' : 'text'}
        enterKeyHint={fine ? 'done' : 'next'}
        placeholder={fine || edit ? undefined : '칸을 한 번 더 누르면 입력'}
        onFocus={() => {
          if (holdBlur.current) return
          const e = editRef.current
          if (!e) begin('bar', 'edit', activeInput)
          else if (e.from === 'cell') setEdit({ ...e, from: 'bar', mode: 'edit' })
        }}
        onChange={(e) => {
          const el = e.currentTarget
          const ev = e.nativeEvent as InputEvent
          const how = ev.inputType
          if (!fine && editRef.current && (how === 'insertLineBreak' || how === 'insertParagraph' || (how === 'insertText' && ev.data === '\n'))) {
            // a keyboard's 다음 that came as a new line
            el.value = dropBreak(el.value, el.selectionStart)
            advance(el.value)
            return
          }
          setDraft(el.value)
          setCaret(el.selectionStart)
          if (point) setPoint(null)
        }}
        onSelect={(e) => setCaret(e.currentTarget.selectionStart)}
        onKeyDown={onBarKey}
        onCompositionStart={() => (composing.current = true)}
        onCompositionEnd={() => (composing.current = false)}
        onBlur={(e) => {
          if (holdBlur.current) return
          composing.current = false
          const next = e.relatedTarget as HTMLElement | null
          if (next?.closest('.g-bar-buttons')) return
          if (editRef.current?.from === 'bar') commit(e.currentTarget.value)
        }}
      />
      {fine && edit?.from === 'bar' && (
        <span className="g-bar-buttons">
          <IconButton label="취소" onClick={cancel}>
            <Icon name="close" size={18} />
          </IconButton>
          <IconButton label="입력" className="primary-icon" onClick={() => commit(draftRef.current)}>
            <Icon name="check" size={18} />
          </IconButton>
        </span>
      )}
      {phoneEdit && (
        <button type="button" className="g-done" aria-label="완료" title="완료" onPointerDown={keepFocus} onClick={done}>
          <Icon name="check" size={20} />
        </button>
      )}
    </div>
  )

  // a phone's row of keys while typing: ✕, the keyboard, where 다음 goes, and = ( ) …
  const phoneKeys = phoneEdit && (
    <div className="g-keys" role="toolbar" aria-label="입력 도구">
      <button type="button" aria-label="취소" title="취소" onPointerDown={keepFocus} onClick={cancel}>
        <Icon name="close" size={18} />
      </button>
      <button
        type="button"
        aria-label={keys === 'num' ? '글자 자판' : '숫자 자판'}
        title={keys === 'num' ? '글자 자판' : '숫자 자판'}
        onPointerDown={keepFocus}
        onClick={flipKeys}
      >
        {keys === 'num' ? '가' : '123'}
      </button>
      <button
        type="button"
        aria-label={dir === 'down' ? '다음 칸: 아래' : '다음 칸: 오른쪽'}
        title={dir === 'down' ? '다음 칸: 아래' : '다음 칸: 오른쪽'}
        onPointerDown={keepFocus}
        onClick={flipDir}
      >
        {dir === 'down' ? '↓' : '→'}
      </button>
      {fx}
      <span className="g-sep" />
      <span className="g-formula-keys">
        {FORMULA_KEYS.map((k) => (
          <button key={k} type="button" onPointerDown={keepFocus} onClick={() => putKey(k)}>
            {k}
          </button>
        ))}
      </span>
    </div>
  )

  return (
    <div className={`g-screen${phoneEdit ? ' g-phone-edit' : ''}`}>
      <div className="g-tools" role="toolbar" aria-label="서식과 표 도구">
        <button type="button" className={`g-tool${st?.b ? ' on' : ''}`} aria-pressed={!!st?.b} aria-label="굵게" title="굵게 (Ctrl+B)" onClick={() => toggle('b')} disabled={readOnly}>
          <b>가</b>
        </button>
        <button type="button" className={`g-tool${st?.i ? ' on' : ''}`} aria-pressed={!!st?.i} aria-label="기울임" title="기울임 (Ctrl+I)" onClick={() => toggle('i')} disabled={readOnly}>
          <i>가</i>
        </button>
        <button type="button" className={`g-tool${st?.u ? ' on' : ''}`} aria-pressed={!!st?.u} aria-label="밑줄" title="밑줄 (Ctrl+U)" onClick={() => toggle('u')} disabled={readOnly}>
          <u>가</u>
        </button>
        <label className="g-tool g-color" title="글자색">
          <span aria-hidden style={{ color: st?.fg ?? undefined }}>A</span>
          <input type="color" aria-label="글자색" value={st?.fg ?? '#000000'} onChange={(e) => style({ fg: e.target.value })} disabled={readOnly} />
        </label>
        <label className="g-tool g-color" title="채우기">
          <span aria-hidden className="g-fill" style={{ background: st?.bg ?? undefined }} />
          <input type="color" aria-label="채우기 색" value={st?.bg ?? '#ffffff'} onChange={(e) => style({ bg: e.target.value })} disabled={readOnly} />
        </label>
        <button type="button" className="g-tool" aria-label="색 지우기" title="색 지우기" onClick={() => style({ fg: '', bg: '' })} disabled={readOnly}>
          <Icon name="close" size={16} />
        </button>
        <select aria-label="맞춤" value={st?.al ?? ''} onChange={(e) => style({ al: e.target.value })} disabled={readOnly}>
          <option value="">맞춤</option>
          <option value="l">왼쪽</option>
          <option value="c">가운데</option>
          <option value="r">오른쪽</option>
        </select>
        <select aria-label="표시 형식" value={NUMBER_FORMATS.some((f) => f.code === (st?.nf ?? '')) ? (st?.nf ?? '') : 'other'} onChange={(e) => e.target.value !== 'other' && style({ nf: e.target.value })} disabled={readOnly}>
          {NUMBER_FORMATS.map((f) => (
            <option key={f.code} value={f.code}>
              {f.label}
            </option>
          ))}
          {!NUMBER_FORMATS.some((f) => f.code === (st?.nf ?? '')) && <option value="other">{st?.nf}</option>}
        </select>
        <span className="g-sep" />
        <button type="button" className="g-tool g-text" onClick={(e) => setMenu({ x: e.clientX, y: e.clientY, area: 'cell' })} disabled={readOnly}>
          행·열
        </button>
        <button type="button" className="g-tool g-text" aria-pressed={!!(sheet.frozen_rows || sheet.frozen_cols)} onClick={freeze}>
          {sheet.frozen_rows || sheet.frozen_cols ? '고정 풀기' : '틀 고정'}
        </button>
        <button type="button" className="g-tool g-text" onClick={() => setChart(true)}>
          차트
        </button>
        <button type="button" className="g-tool g-text" onClick={() => setNames(true)}>
          이름
        </button>
        <span className="g-sep" />
        <button type="button" className="g-tool g-text" onClick={() => file.current?.click()} disabled={!!busyText}>
          가져오기
        </button>
        <button type="button" className="g-tool g-text" onClick={() => void exportFile()} disabled={!!busyText}>
          내보내기
        </button>
        <input ref={file} type="file" accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" hidden aria-label="xlsx 파일" onChange={(e) => {
          const f = e.target.files?.[0]
          e.target.value = ''
          if (f) void importFile(f)
        }} />
      </div>
      {fine && formulaBar}
      {fine && (
        // over the grid, which does not move as they come and go
        <div className="g-help-anchor">
          <div className="g-help">
            {funcs}
            {guide}
          </div>
        </div>
      )}
      {(busyText || pending > 0 || readOnly) && (
        <div className="g-status" role="status">
          {busyText ?? (readOnly ? '행·열을 옮기는 중…' : `보내는 중 ${pending}`)}
        </div>
      )}
      <GridCanvas
        store={store}
        sheet={sheet}
        widths={widths}
        sel={sel}
        editing={edit?.from === 'cell'}
        editorRef={editor}
        editorProps={editorProps}
        fine={fine}
        onSelect={select}
        onTap={onTap}
        keepFocus={phoneEdit}
        preview={phoneEdit ? draft : null}
        pointRect={phoneEdit ? (point?.rect ?? null) : null}
        onMenu={(x, y, area) => setMenu({ x, y, area })}
        onResize={onResize}
        reveal={reveal}
        overlay={(v) => (
          <ChartsOverlay store={store} sheet={sheet} view={v} fine={fine} onChange={(charts) => patchSheet({ charts: charts as never })} />
        )}
      />
      {!fine && (
        <div className="g-entry">
          {funcs}
          {guide}
          {formulaBar}
          {phoneKeys}
        </div>
      )}
      <nav className="g-tabs" aria-label="시트">
        {sheets.map((s) => (
          <button
            key={s.id}
            type="button"
            aria-pressed={s.id === sheet.id}
            onClick={() => {
              if (s.id === sheet.id) setSheetMenu(s)
              else {
                if (edit) commit(edit.from === 'cell' ? (editor.current?.value ?? draftRef.current) : draftRef.current)
                setSheetId(s.id)
                setSel(cellSel(0, 0))
              }
            }}
          >
            {s.name}
          </button>
        ))}
        <IconButton label="시트 더하기" onClick={() => setNewSheet(true)}>
          <Icon name="plus" size={18} />
        </IconButton>
      </nav>
      <PopMenu at={menu} items={menuItems} title="행·열" sheet={!fine} onClose={() => setMenu(null)} />
      <SheetMenu store={store} sheet={sheetMenu} onClose={() => setSheetMenu(null)} onDeleted={() => setSheetId(0)} />
      <NamesSheet store={store} open={names} onClose={() => setNames(false)} />
      <NewSheetSheet open={newSheet} onClose={() => setNewSheet(false)} onBlank={() => void addSheet()} onTemplate={(t) => void addFromTemplate(t)} />
      <ChartSheet open={chart} range={rectName(clampRect(rect, store.used(sheet.id)))} onClose={() => setChart(false)} onCreate={createChart} />
      <ImportResult result={imported} onClose={() => setImported(null)} />
    </div>
  )
}

function monthNow(): string {
  const d = new Date((kstClock().today - 25569) * 86400000)
  return `${d.getUTCFullYear()}-${String(d.getUTCMonth() + 1).padStart(2, '0')}`
}

